import { useCallback, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { DropdownMenu } from '@components/DropdownMenu/DropdownMenu';
import { Dropdown } from '@components/DropdownMenu/Dropdown';
import type {
  MultiSelectProps,
  MultiSelectOption,
  MultiSelectOptions,
} from './types';
import { SelectionContainer } from './SelectionContainer';
import { EmptyState } from './EmptyState';
import { escapeRegExp } from '@utils/string';
import { Button } from '@components/Button/Button';
import { Badge } from '@components/Badge/Badge';
import { PlanBadge } from '@components/PlanBadge/PlanBadge';

export type {
  MultiSelectProps,
  MultiSelectOption,
  MultiSelectOptions,
} from './types';

const StyledDropdown = styled(Dropdown)<{ actualWidth: () => number }>`
  list-style: none;
  width: ${({ actualWidth }) => actualWidth()}px;
`;

const ItemList = styled('ul')`
  list-style: none;
  margin: 0;
  padding: ${tokens.spacer.x1} 0;
`;

const InfoFooter = styled.div`
  ${tokens.text('caption')};
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2};
  background-color: ${tokens.ALIAS_COLORS.bg_info};
  color: ${tokens.color.text_light};
  user-select: none;
`;

const AddOptionFooter = styled.div`
  user-select: none;
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const DropdownContainer = styled.div`
  overflow-y: auto;
  max-height: 440px;
`;

const AddButton = styled(Button)`
  width: 100%;
`;

const BadgeContainer = styled.div`
  position: absolute;
  right: 0px;
  padding: 0px 16px;
`;

const MAX_OPTIONS_RENDERED = 500;

/**
 * MultiSelect component.
 *
 * {@link https://zeroheight.com/76c439891/p/79ef8f-multiselect-input Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-multiselect Code documentation}
 *
 * @example
 * const [tags, setTags] = useState([{value: 'option', label: 'Option'}]);
 * return <MultiSelect selectedOptions={tags} onChange={setTags} />;
 */
export const MultiSelect = ({
  availableOptions,
  className,
  onChange,
  selectedOptions,
  placeholder,
  isDisabled = false,
  isInvalid = false,
  size = 'm',
  hasAddOption = false,
  onAddOption,
  addOptionBadge,
  addOptionPlanBadge,
}: MultiSelectProps): JSX.Element => {
  const [filter, setFilter] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const selectRef = useRef<HTMLDivElement>();
  const filterInputRef = useRef<HTMLInputElement>();

  // when a option is clicked in the dropdown
  const selectOptionHandler = useCallback(
    (option: MultiSelectOption): void => {
      // focus on input
      filterInputRef.current.focus();

      if (!onChange) {
        return;
      }

      const newSelectedOptions = selectedOptions
        ? selectedOptions.map((o) => o)
        : [];
      newSelectedOptions.push(option);

      onChange(newSelectedOptions);

      // clear input value
      setFilter('');
    },
    [selectedOptions, onChange],
  );

  // filtering
  const optionsFilter = useCallback(
    (option: MultiSelectOption): boolean => {
      const keepOption =
        !selectedOptions ||
        !selectedOptions.find(
          (selectedOption) => selectedOption.value === option.value,
        );

      if (!filter) {
        return keepOption;
      }

      return (
        keepOption && new RegExp(escapeRegExp(filter), 'i').test(option.label)
      );
    },
    [selectedOptions, filter],
  );

  // when an option is removed from
  const onOptionsChange = useCallback(
    (options: MultiSelectOptions): void => {
      if (!onChange) {
        return;
      }

      onChange(options);

      if (dropdownVisible) {
        filterInputRef.current.focus();
      }
    },
    [onChange, dropdownVisible],
  );

  const filteredOptions = useMemo(() => {
    if (!availableOptions) {
      return [];
    }

    return availableOptions.filter(optionsFilter);
  }, [availableOptions, optionsFilter]);

  const isExactMatch = useMemo(() => {
    if (!filteredOptions.length) {
      return false;
    }

    return !!filteredOptions.find(
      (option) =>
        option.label.toLowerCase().trim() === filter.toLowerCase().trim(),
    );
  }, [filteredOptions, filter]);

  const isAlreadySelected = useMemo(() => {
    if (!selectedOptions?.length) {
      return false;
    }

    return !!selectedOptions.find(
      (option) => option.label.toLowerCase() === filter.toLowerCase(),
    );
  }, [selectedOptions, filter]);

  // disabled state
  if (isDisabled) {
    return (
      <SelectionContainer
        placeholder={placeholder}
        isDisabled
        isInvalid={isInvalid}
        selectedOptions={selectedOptions}
      />
    );
  }

  const filteredOptionsToRender = filteredOptions.slice(
    0,
    MAX_OPTIONS_RENDERED,
  );
  const items = filteredOptionsToRender.map((option) => (
    <DropdownMenu.Item
      key={option.value}
      onClick={() => selectOptionHandler(option)}
      icon={option.icon}
    >
      {option.label}
    </DropdownMenu.Item>
  ));

  return (
    <StyledDropdown
      appendTo={() => selectRef.current}
      maxWidth="none"
      actualWidth={() => selectRef?.current?.clientWidth}
      trigger={
        <SelectionContainer
          ref={selectRef}
          inputRef={filterInputRef}
          placeholder={placeholder}
          isInvalid={isInvalid}
          selectedOptions={selectedOptions}
          onChange={onOptionsChange}
          filterValue={filter}
          onFilterFocus={() => setDropdownVisible(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as HTMLElement)) {
              setDropdownVisible(false);
            }
          }}
          onFilterChange={(event) => {
            setFilter(event.target.value);
          }}
          size={size}
          className={className}
        />
      }
      onClickOutside={() => setDropdownVisible(false)}
      visible={dropdownVisible}
      triggerEvent="none"
    >
      {filteredOptions.length === 0 && <EmptyState searchTerm={filter} />}
      {filteredOptions.length > 0 && (
        <DropdownContainer data-testid="multiselect_items">
          <ItemList>{items}</ItemList>{' '}
        </DropdownContainer>
      )}
      {filteredOptions.length > MAX_OPTIONS_RENDERED && (
        <InfoFooter>
          Showing {MAX_OPTIONS_RENDERED} items only. Type to search for more...
        </InfoFooter>
      )}
      {hasAddOption &&
        onAddOption &&
        filter &&
        !isExactMatch &&
        !isAlreadySelected && (
          <AddOptionFooter>
            <AddButton
              data-testid="Add option"
              kind="ghost_standout"
              icon="plus"
              onClick={() => {
                onAddOption(filter);
                setFilter('');
              }}
            >{`Add "${filter}"`}</AddButton>
            {(addOptionBadge || addOptionPlanBadge) && (
              <BadgeContainer>
                {addOptionBadge ? (
                  <Badge kind={addOptionBadge} data-testid="badge" />
                ) : (
                  <PlanBadge
                    kind={addOptionPlanBadge}
                    data-testid="plan-badge"
                  />
                )}
              </BadgeContainer>
            )}
          </AddOptionFooter>
        )}
    </StyledDropdown>
  );
};
